import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { telegram } from '../telegram/adapter';

export type GestureDirection = 'down' | 'up' | 'left' | 'right' | null;

interface GestureOptions {
  disabled?: boolean;
  onDown: () => void;
  onUp: () => void;
  onLeft: () => void;
  onRight: () => void;
}

const THRESHOLD = 58;

export function useGestures(options: GestureOptions) {
  const origin = useRef<{ x: number; y: number; pointerId: number } | null>(null);
  const locked = useRef<'horizontal' | 'vertical' | null>(null);
  const crossed = useRef(false);
  const dragRef = useRef({ x: 0, y: 0, direction: null as GestureDirection, ready: false });
  const optionsRef = useRef(options);
  const [drag, setDrag] = useState({ x: 0, y: 0, direction: null as GestureDirection, ready: false });
  optionsRef.current = options;

  const reset = useCallback(() => {
    origin.current = null;
    locked.current = null;
    crossed.current = false;
    dragRef.current = { x: 0, y: 0, direction: null, ready: false };
    setDrag(dragRef.current);
  }, []);

  const finishGesture = useCallback(() => {
    if (!origin.current) return;
    const latest = dragRef.current;
    const action = latest.ready ? latest.direction : null;
    reset();
    const latestOptions = optionsRef.current;
    if (action === 'down') latestOptions.onDown();
    if (action === 'up') latestOptions.onUp();
    if (action === 'left') latestOptions.onLeft();
    if (action === 'right') latestOptions.onRight();
  }, [reset]);

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (options.disabled || event.pointerType === 'mouse' && event.button !== 0) return;
    if ((event.target as HTMLElement).closest('button, a, select')) return;
    origin.current = { x: event.clientX, y: event.clientY, pointerId: event.pointerId };
    event.currentTarget.setPointerCapture(event.pointerId);
    window.addEventListener('pointerup', finishGesture, { once: true });
    window.addEventListener('mouseup', finishGesture, { once: true });
    document.addEventListener('pointerup', finishGesture, { capture: true, once: true });
    document.addEventListener('mouseup', finishGesture, { capture: true, once: true });
  }, [finishGesture, options.disabled]);

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (!origin.current || origin.current.pointerId !== event.pointerId) return;
    event.preventDefault();
    const x = event.clientX - origin.current.x;
    const y = event.clientY - origin.current.y;
    if (!locked.current && Math.max(Math.abs(x), Math.abs(y)) > 8) locked.current = Math.abs(x) > Math.abs(y) ? 'horizontal' : 'vertical';
    const dx = locked.current === 'horizontal' ? x : 0;
    const dy = locked.current === 'vertical' ? y : 0;
    const distance = Math.max(Math.abs(dx), Math.abs(dy));
    const direction: GestureDirection = locked.current === 'horizontal' ? (dx >= 0 ? 'right' : 'left') : locked.current === 'vertical' ? (dy >= 0 ? 'down' : 'up') : null;
    const ready = distance >= THRESHOLD;
    if (ready && !crossed.current) {
      crossed.current = true;
      telegram.haptic('light');
    } else if (!ready) crossed.current = false;
    dragRef.current = { x: dx, y: dy, direction, ready };
    setDrag(dragRef.current);
  }, []);

  const onPointerEnd = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (!origin.current || origin.current.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    finishGesture();
  }, [finishGesture]);

  const onMouseUp = useCallback(() => {
    if (!origin.current) return;
    finishGesture();
  }, [finishGesture]);

  return { drag, bind: { onPointerDown, onPointerMove, onPointerUp: onPointerEnd, onPointerCancel: finishGesture, onMouseUp } };
}
